import { z } from 'zod';

/**
 * Imagens carregadas no back-office (fotos dos produtos, eventos e
 * passatempos), ver docs/imagens.
 *
 *   - `POST /api/site/media` (gestao, staff com `store.manage`, `events.manage`,
 *     `giveaways.manage` ou `content.manage`): corpo = os bytes da imagem, com
 *     `Content-Type: image/jpeg | image/png | image/webp`. Responde `MediaImage`.
 *   - `GET /api/media/<id>/<sm|md|lg>.webp` (publico, site e gestao): a imagem,
 *     com cache longa. Uma imagem nunca muda: trocar a foto = carregar outra.
 *
 * O que se guarda nos produtos, eventos e passatempos e o URL do tamanho `md`.
 */

export const MEDIA_SIZES = ['sm', 'md', 'lg'] as const;
export type MediaSize = (typeof MEDIA_SIZES)[number];

/** Maior ficheiro aceite (igual ao limite da API). */
export const MEDIA_MAX_BYTES = 10 * 1024 * 1024;
export const MEDIA_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Fotos por produto. */
export const PRODUCT_IMAGES_MAX = 8;

export function mediaUrl(id: string, size: MediaSize = 'md'): string {
  return `/api/media/${id}/${size}.webp`;
}

const MEDIA_URL = /^\/api\/media\/([0-9a-f-]{36})\/(sm|md|lg)\.webp$/;

/**
 * A mesma imagem noutro tamanho. Um URL externo (as imagens de antes do
 * upload, `https://...`) fica igual: so ha esse.
 */
export function mediaVariant(url: string, size: MediaSize): string {
  const match = MEDIA_URL.exec(url);
  return match ? mediaUrl(match[1] as string, size) : url;
}

export const mediaImageSchema = z.object({
  id: z.string().uuid(),
  /** URL do tamanho `md`, o que se guarda. */
  url: z.string(),
  /** Dimensoes da imagem original (depois de rodada). */
  width: z.number().int(),
  height: z.number().int(),
});
export type MediaImage = z.infer<typeof mediaImageSchema>;
